import { useMemo, useState } from 'react';
import { evaluateLineCondition, evaluateViewCondition, resolveLineValues } from '@ayumia/component-runtime';
import { text } from '../app/i18n';
import { Drawer } from '../design-system/Overlay';
import { Pill, type PillTone } from '../design-system/primitives';
import { Empty, errorView, Loading } from '../states/States';
import { compareAbsDecimalText, isDecimalText, isZeroDecimalText, parseDecimalInput, sumDecimalText } from './decimal-text';
import type { DetailInputs } from './assembly';
import { formatDecimal } from './format';
import { shortId, useReferenceLabels } from './reference-labels';
import { useLoad } from './useLoad';
import { useAppServices } from './ViewContext';

/**
 * Onglet `lines` du Detail (ADR-133 §4) : un champ tableau de l'enregistrement en table aux colonnes
 * déclarées. Une référence s'affiche par le libellé de l'objet cité, lu sous l'autorisation de
 * l'utilisateur ; les décimaux restent exacts, totaux compris (R2). Avec `decision`, chaque ligne
 * concernée porte un choix qui alimente la charge utile d'une action (`requires_complete`) ; avec
 * `row_detail`, un clic ouvre l'historique de la position. Présentation : le serveur revalide tout.
 */

export type LinesTabInputs = DetailInputs['tabs'][number];
type Column = NonNullable<LinesTabInputs['columns']>[number];
type Decision = NonNullable<LinesTabInputs['decision']>;
type Row = Record<string, unknown>;
type Tone = NonNullable<Decision['options'][number]['tone']>;

const TONE_TO_PILL: Readonly<Record<Tone, PillTone>> = { ok: 'success', warn: 'warn', danger: 'danger', info: 'info', neutral: 'neutral' };

/** Choix en cours d'un onglet : clé de ligne → valeur d'option. */
export type LineChoices = Readonly<Record<string, string>>;

/** Charge utile qu'un onglet `lines` prépare pour une action (`requires_complete`). */
export interface LineDraft {
  payload_field: string;
  entries: Row[];
  applicable: number;
  missing: number;
}

function valueAt(row: unknown, path: string): unknown {
  let current: unknown = row;
  for (const segment of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Row)[segment];
  }
  return current;
}

function rowsOf(record: Row, tab: LinesTabInputs): Row[] {
  const value = tab.field === undefined ? undefined : record[tab.field];
  return Array.isArray(value) ? value.filter((entry): entry is Row => entry !== null && typeof entry === 'object') : [];
}

const keyOf = (row: Row, decision: Decision): string => JSON.stringify(decision.key.map((path) => valueAt(row, path) ?? null));

function applies(row: Row, decision: Decision, record: Row, context: Readonly<Record<string, unknown>>): boolean {
  return evaluateLineCondition(decision.applies_when, { line: row, record, context });
}

/** La charge utile préparée par l'onglet, et ce qu'il reste à décider : une ligne concernée sans choix bloque l'action. */
export function lineDraft(tab: LinesTabInputs, record: Row, choices: LineChoices, context: Readonly<Record<string, unknown>>): LineDraft | null {
  const decision = tab.decision;
  if (decision === undefined) return null;
  const valueField = decision.value_field ?? 'decision';
  const entries: Row[] = [];
  let applicable = 0;
  for (const row of rowsOf(record, tab)) {
    if (!applies(row, decision, record, context)) continue;
    applicable += 1;
    const choice = choices[keyOf(row, decision)];
    if (choice === undefined) continue;
    const entry: Row = {};
    // Chaque entrée reprend les champs de premier niveau qui identifient la ligne (une référence entière, pas son seul identifiant).
    for (const top of new Set(decision.key.map((path) => path.split('.')[0]!))) {
      const value = row[top];
      if (value !== undefined && value !== null) entry[top] = value;
    }
    entry[valueField] = choice;
    entries.push(entry);
  }
  return { payload_field: decision.payload_field, entries, applicable, missing: applicable - entries.length };
}

function referenceOf(value: unknown, column: Column): { type: string; id: string } | null {
  if (typeof value === 'string' && column.reference_type !== undefined) return { type: column.reference_type, id: value };
  if (value !== null && typeof value === 'object' && typeof (value as Row)['type'] === 'string' && typeof (value as Row)['id'] === 'string') return { type: (value as Row)['type'] as string, id: (value as Row)['id'] as string };
  return null;
}

function decimalText(value: string, signed: boolean): string {
  const shown = formatDecimal(value, 'fr', { maximumFractionDigits: 6 });
  return signed && !value.startsWith('-') && !isZeroDecimalText(value) ? `+${shown}` : shown;
}

export function LinesTab({ tab, record, choices, onChoices }: { tab: LinesTabInputs; record: Row; choices: LineChoices; onChoices: (next: LineChoices) => void }) {
  const services = useAppServices();
  const [openRow, setOpenRow] = useState<Row | null>(null);
  const [threshold, setThreshold] = useState('');
  const rows = useMemo(() => rowsOf(record, tab), [record, tab]);
  const columns = tab.columns ?? [];
  const decision = tab.decision;
  const editable = decision !== undefined && evaluateViewCondition(decision.editable_when, { record, context: services.context });

  const wanted = useMemo(() => {
    const byType = new Map<string, Set<string>>();
    for (const row of rows) {
      for (const column of columns) {
        if (column.format !== 'reference') continue;
        const ref = referenceOf(valueAt(row, column.path), column);
        if (ref === null) continue;
        byType.set(ref.type, (byType.get(ref.type) ?? new Set()).add(ref.id));
      }
    }
    return [...byType.entries()].map(([type, ids]) => ({ type, ids: [...ids] }));
  }, [rows, columns]);
  const labels = useReferenceLabels(services.client, wanted);

  if (rows.length === 0) return <Empty label="Aucune ligne." />;

  const plain = (row: Row, column: Column): string => {
    const value = valueAt(row, column.path);
    if (value === undefined || value === null) return '—';
    if (column.format === 'reference') {
      const ref = referenceOf(value, column);
      return ref === null ? '—' : (labels.get(ref.id) ?? shortId(ref.id));
    }
    if (column.format === 'enum' && typeof value === 'string') return text(column.enum_labels?.[value], 'fr', value);
    return typeof value === 'string' || typeof value === 'number' ? String(value) : JSON.stringify(value);
  };
  const cell = (row: Row, column: Column) => {
    const value = valueAt(row, column.path);
    if (value === undefined || value === null) return '—';
    switch (column.format) {
      case 'reference':
        return plain(row, column);
      case 'decimal': {
        if (!isDecimalText(value) && typeof value !== 'number') return String(value);
        const exact = typeof value === 'number' ? String(value) : value;
        const unit = column.unit_path === undefined ? undefined : valueAt(row, column.unit_path);
        const shown = `${decimalText(exact, column.signed === true)}${typeof unit === 'string' ? ` ${unit}` : ''}`;
        if (column.tone_by_sign !== true || isZeroDecimalText(exact)) return shown;
        return <span className={exact.startsWith('-') ? 'ay-tone--negative' : 'ay-tone--positive'}>{shown}</span>;
      }
      case 'enum': {
        const label = typeof value === 'string' ? text(column.enum_labels?.[value], 'fr', value) : String(value);
        const tone = typeof value === 'string' ? column.tones?.[value] : undefined;
        return tone === undefined ? label : <Pill tone={TONE_TO_PILL[tone]}>{label}</Pill>;
      }
      default:
        return typeof value === 'string' || typeof value === 'number' ? String(value) : JSON.stringify(value);
    }
  };

  // Totaux exacts, par unité (unit_path de la colonne) : jamais d'addition en `number`.
  const totals = (tab.totals ?? []).map((path) => {
    const column = columns.find((entry) => entry.path === path);
    const byUnit = new Map<string, string[]>();
    for (const row of rows) {
      const value = valueAt(row, path);
      if (!isDecimalText(value)) continue;
      const unit = column?.unit_path === undefined ? '' : String(valueAt(row, column.unit_path) ?? '');
      byUnit.set(unit, [...(byUnit.get(unit) ?? []), value]);
    }
    return { path, text: [...byUnit.entries()].map(([unit, values]) => `${decimalText(sumDecimalText(values), column?.signed === true)}${unit === '' ? '' : ` ${unit}`}`).join(' · ') };
  });

  const draft = lineDraft(tab, record, choices, services.context);
  const choose = (row: Row, value: string) => decision !== undefined && onChoices({ ...choices, [keyOf(row, decision)]: value });
  const applyToAll = (value: string, filter: (row: Row) => boolean = () => true) => {
    if (decision === undefined) return;
    const next: Record<string, string> = { ...choices };
    for (const row of rows) if (applies(row, decision, record, services.context) && filter(row)) next[keyOf(row, decision)] = value;
    onChoices(next);
  };
  const below = decision?.bulk?.below;
  const limit = parseDecimalInput(threshold);
  const optionOf = (value: unknown) => decision?.options.find((option) => option.value === value);

  return (
    <div className="ay-lines">
      {decision !== undefined && editable && (
        <div className="ay-lines__toolbar" role="toolbar" aria-label="Décision par ligne">
          {decision.bulk?.all !== undefined && (
            <button type="button" className="ay-button ay-button--secondary" onClick={() => applyToAll(decision.bulk!.all!)}>
              {`${text(optionOf(decision.bulk.all)?.label, 'fr', decision.bulk.all)} : toutes les lignes`}
            </button>
          )}
          {below !== undefined && (
            <form
              className="ay-lines__threshold"
              onSubmit={(event) => {
                event.preventDefault();
                if (limit === null) return;
                applyToAll(below.option, (row) => {
                  const value = valueAt(row, below.path);
                  return isDecimalText(value) && compareAbsDecimalText(value, limit) < 0;
                });
              }}
            >
              <label>
                {text(below.label)}
                <input inputMode="decimal" value={threshold} onChange={(event) => setThreshold(event.target.value)} aria-invalid={threshold !== '' && limit === null} placeholder="0" />
              </label>
              <button type="submit" className="ay-button ay-button--secondary" disabled={limit === null}>
                Appliquer
              </button>
            </form>
          )}
          {draft !== null && (
            <span className="ay-lines__tally" role="status">
              {decision.options.map((option) => `${draft.entries.filter((entry) => entry[decision.value_field ?? 'decision'] === option.value).length} ${text(option.label).toLowerCase()}`).join(' · ')} · {draft.missing} sans décision
            </span>
          )}
        </div>
      )}
      <div className="ay-table-wrap">
        <table className="ay-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.path} scope="col" className={column.format === 'decimal' ? 'ay-col--end ay-num' : undefined}>
                  {text(column.label)}
                </th>
              ))}
              {decision !== undefined && <th scope="col">Décision</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const concerned = decision !== undefined && applies(row, decision, record, services.context);
              const key = decision === undefined ? String(index) : keyOf(row, decision);
              const current = choices[key] ?? (decision?.recorded_path === undefined ? undefined : valueAt(row, decision.recorded_path));
              return (
                <tr key={key}>
                  {columns.map((column, position) => (
                    <td key={column.path} className={column.format === 'decimal' ? 'ay-col--end ay-num' : undefined}>
                      {position === 0 && tab.row_detail !== undefined ? (
                        <button type="button" className="ay-link-button" onClick={() => setOpenRow(row)}>
                          {cell(row, column)}
                        </button>
                      ) : (
                        cell(row, column)
                      )}
                    </td>
                  ))}
                  {decision !== undefined && (
                    <td>
                      {!concerned ? (
                        '—'
                      ) : editable ? (
                        <div className="ay-segmented ay-segmented--light" role="radiogroup" aria-label={`Décision, ligne ${index + 1}`}>
                          {decision.options.map((option) => (
                            <button key={option.value} type="button" role="radio" className="ay-segmented__option" aria-checked={current === option.value} onClick={() => choose(row, option.value)}>
                              {text(option.label)}
                            </button>
                          ))}
                        </div>
                      ) : current === undefined ? (
                        '—'
                      ) : (
                        <Pill tone={TONE_TO_PILL[optionOf(current)?.tone ?? 'neutral']}>{text(optionOf(current)?.label, 'fr', String(current))}</Pill>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {totals.length > 0 && (
            <tfoot>
              <tr>
                {columns.map((column, position) => {
                  const total = totals.find((entry) => entry.path === column.path);
                  return (
                    <td key={column.path} className={column.format === 'decimal' ? 'ay-col--end ay-num' : undefined}>
                      {position === 0 ? <strong>{`Total · ${rows.length} ligne(s)`}</strong> : total === undefined ? null : <strong>{total.text}</strong>}
                    </td>
                  );
                })}
                {decision !== undefined && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {tab.row_detail !== undefined && (
        <Drawer title={text(tab.row_detail.title, 'fr', 'Historique de la position')} kicker={openRow === null ? '' : columns.filter((column) => column.format === 'reference').map((column) => plain(openRow, column)).join(' · ')} open={openRow !== null} onClose={() => setOpenRow(null)}>
          {openRow !== null && <PositionHistory parameters={resolveLineValues(tab.row_detail.parameters, { line: openRow, record, context: services.context })} />}
        </Drawer>
      )}
    </div>
  );
}

interface PositionMovement {
  movement_id: string;
  reference: string;
  occurred_at: string;
  movement_type: string;
  movement_semantic?: string;
  quantity: string;
  running_total: string;
  other_location_id?: string;
  counterpart?: string;
}

const MOVEMENT_LABELS: Readonly<Record<string, string>> = {
  goods_receipt: 'Réception',
  unplanned_receipt: 'Entrée',
  inter_location_transfer: 'Transfert',
  inventory_gain: 'Gain d’inventaire',
  inventory_loss: 'Perte d’inventaire',
  production_consumption: 'Consommation en production',
  production_output: 'Production',
  customer_delivery: 'Expédition',
  scrap: 'Mise au rebut',
  opening: 'Ouverture',
  receipt: 'Réception',
  shipment: 'Expédition',
  transfer: 'Transfert',
  adjustment: 'Ajustement',
  consumption: 'Consommation',
  reversal: 'Inversion',
  state_change: 'Changement d’état',
  return: 'Retour',
};

/** Historique d'une position (`GetPositionHistory`, ADR-133 §3) : relu dans le ledger, cumul jusqu'à l'instant du comptage. */
function PositionHistory({ parameters }: { parameters: Readonly<Record<string, unknown>> }) {
  const services = useAppServices();
  const query = Object.fromEntries(Object.entries(parameters).filter(([, value]) => typeof value === 'string')) as Record<string, string>;
  const [loaded, reload] = useLoad<{ until: string; unit: string | null; total: string; truncated: boolean; movements: PositionMovement[] }>(`position:${JSON.stringify(query)}`, async () => (await services.client.query('GetPositionHistory', query)).data as never);
  const locations = useReferenceLabels(services.client, loaded.status === 'ready' ? [{ type: 'Location', ids: [...new Set(loaded.value.movements.flatMap((entry) => (entry.other_location_id === undefined ? [] : [entry.other_location_id])))] }] : []);
  if (loaded.status === 'loading') return <Loading />;
  if (loaded.status === 'error') return <>{errorView(loaded.error, reload)}</>;
  const { movements, total, unit, truncated, until } = loaded.value;
  const suffix = unit === null ? '' : ` ${unit}`;
  if (movements.length === 0) return <Empty label="Aucun mouvement sur cette position jusqu’au comptage." />;
  const entries = movements.filter((entry) => !entry.quantity.startsWith('-')).length;
  const exits = movements.length - entries;
  return (
    <div className="ay-position">
      <p className="ay-position__summary">
        {`${formatDecimal(total, 'fr', { maximumFractionDigits: 6 })}${suffix} au ${new Date(until).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })} · ${entries} entrée(s), ${exits} sortie(s)`}
      </p>
      {exits === 0 && <p className="ay-banner ay-banner--warning">Aucune sortie enregistrée sur cette position : ce qui en est parti n’a pas laissé de mouvement.</p>}
      <ol className="ay-history">
        {movements.map((entry) => (
          <li key={entry.movement_id} className="ay-history__entry">
            <span className="ay-history__dot" aria-hidden="true" />
            <span className="ay-history__body">
              <strong>
                {MOVEMENT_LABELS[entry.movement_semantic ?? ''] ?? MOVEMENT_LABELS[entry.movement_type] ?? entry.movement_type}
                {entry.other_location_id === undefined ? '' : ` · ${entry.quantity.startsWith('-') ? 'vers' : 'depuis'} ${locations.get(entry.other_location_id) ?? shortId(entry.other_location_id)}`}
                {` : ${decimalText(entry.quantity, true)}${suffix}`}
              </strong>
              <span className="ay-history__meta">{`${new Date(entry.occurred_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })} · ${entry.reference} · cumul ${formatDecimal(entry.running_total, 'fr', { maximumFractionDigits: 6 })}${suffix}`}</span>
            </span>
          </li>
        ))}
      </ol>
      {truncated && <p className="ay-text--muted">Seuls les 500 premiers mouvements sont montrés.</p>}
    </div>
  );
}
